import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Admin.css';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [college, setCollege] = useState('Thiagarajar College of Engineering (TCE), Madurai');
  const [year, setYear] = useState('2026');
  const [departments, setDepartments] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [logs, setLogs] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [abortController] = useState(null);
  const [exportStatus, setExportStatus] = useState('idle'); // idle, processing, completed, error, cancelled
  const [eventSource, setEventSource] = useState(null);
  const [progressData, setProgressData] = useState(null);

  useEffect(() => {
    // Check if already authenticated
    const token = localStorage.getItem('adminToken');
    if (token === 'admin-token') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/points/admin/login', { password });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        setIsAuthenticated(true);
        setError('');
      }
    } catch (error) {
      setError('Invalid password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setPassword('');
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const handleStart = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    setIsProcessing(true);
    setLogs([]);
    setDownloadUrl('');
    setExportStatus('processing');
    setProgressData(null);
    
    const deptParam = departments.trim() === '' ? 'all' : departments.trim();
    
    addLog(`Starting export for ${college}, Year: ${year}, Departments: ${deptParam === 'all' ? 'All' : deptParam}`, 'info');
    
    try {
      // Use Server-Sent Events for real-time progress
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
      const streamUrl = `${baseUrl}/api/points/export/stream/${encodeURIComponent(college)}/${year}/${encodeURIComponent(deptParam)}`;
      const es = new EventSource(streamUrl);
      setEventSource(es);
      
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'info':
              addLog(data.message, 'info');
              break;
            case 'progress':
              addLog(`📊 ${data.message}`, 'progress');
              setProgressData(data);
              break;
            case 'complete':
              addLog('✅ Export batch completed successfully!', 'success');
              addLog('📊 Excel file is ready for download', 'success');
              
              // Create download URL from base64 data
              const byteCharacters = atob(data.data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
              });
              const url = window.URL.createObjectURL(blob);
              setDownloadUrl(url);
              
              setIsProcessing(false);
              setExportStatus('completed');
              es.close();
              setEventSource(null);
              break;
            case 'error':
              addLog(`❌ Error: ${data.message}`, 'error');
              setExportStatus('error');
              setIsProcessing(false);
              es.close();
              setEventSource(null);
              break;
            case 'warning':
              addLog(`⚠️ ${data.message}`, 'warning');
              if (data.message.includes('cancelled')) {
                setExportStatus('cancelled');
                setIsProcessing(false);
                es.close();
                setEventSource(null);
              }
              break;
            default:
              addLog(data.message, 'info');
          }
        } catch (error) {
          console.error('Error parsing SSE data:', error);
        }
      };
      
      es.onerror = (error) => {
        console.error('EventSource error:', error);
        addLog('❌ Connection error occurred', 'error');
        setExportStatus('error');
        setIsProcessing(false);
        es.close();
        setEventSource(null);
      };
      
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
      setExportStatus('error');
      setIsProcessing(false);
    }
  };

  const handleStop = () => {
    if (eventSource) {
      addLog('🛑 Cancelling export request...', 'warning');
      eventSource.close();
      setEventSource(null);
    }
    if (abortController) {
      abortController.abort();
    }
    setIsProcessing(false);
    setExportStatus('cancelled');
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `export_${college.replace(/[^a-zA-Z0-9]/g, '_')}_${year}_${departments || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      addLog('📥 File downloaded successfully', 'success');
    }
  };

  // Cleanup function for EventSource and AbortController
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
      if (abortController) {
        abortController.abort();
      }
    };
  }, [eventSource, abortController]);

  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="login-box">
          <h2>Admin Login</h2>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter admin password"
              id="admin_password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="submit" className="login-btn">Login</button>
            {error && <p className="error-message">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Export Dashboard</h1>
        <div className="header-buttons">
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </div>
      
      <div className="admin-content">
        <div className="control-panel">
          <h2>Export Configuration</h2>
          
          <div className="form-group">
            <label>College Name:</label>
            <input
              type="text"
              value={college}
              onChange={(e) => setCollege(e.target.value)}
              disabled={isProcessing}
            />
          </div>
          
          <div className="form-group">
            <label>Year:</label>
            <input
              type="text"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              disabled={isProcessing}
            />
          </div>
          
          <div className="form-group">
            <label>Departments (comma-separated, leave empty for all):</label>
            <input
              type="text"
              placeholder="e.g., IT,CSE,ECE or leave empty for all"
              value={departments}
              onChange={(e) => setDepartments(e.target.value)}
              disabled={isProcessing}
            />
            <small>Examples: IT | CSE,ECE | Leave empty for all departments</small>
          </div>
          
          <div className="status-indicator">
            <div className={`status-badge status-${exportStatus}`}>
              {exportStatus === 'idle' && '⚪ Ready'}
              {exportStatus === 'processing' && '🟡 Processing...'}
              {exportStatus === 'completed' && '🟢 Completed'}
              {exportStatus === 'error' && '🔴 Error'}
              {exportStatus === 'cancelled' && '🟠 Cancelled'}
            </div>
          </div>

          {progressData && (
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${progressData.progress || 0}%` }}
                ></div>
              </div>
              <div className="progress-text">
                {progressData.progress || 0}% Complete
              </div>
            </div>
          )}

          <div className="action-buttons">
            {!isProcessing ? (
              <button onClick={handleStart} className="start-btn">Start Export</button>
            ) : (
              <button onClick={handleStop} className="stop-btn">Stop Export</button>
            )}
          </div>
          
          {downloadUrl && (
            <div className="download-section">
              <button onClick={handleDownload} className="download-btn">
                📥 Download Excel File
              </button>
            </div>
          )}
        </div>
        
        <div className="logs-panel">
          <h2>Process Logs</h2>
          <div className="logs-container">
            {logs.length === 0 ? (
              <p className="no-logs">No logs yet. Start an export to see logs.</p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className={`log-entry log-${log.type}`}>
                  <span className="log-time">[{log.timestamp}]</span>
                  <span className="log-message">{log.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      {showConfirmation && (
        <div className="admin-confirmation-modal">
          <div className="admin-modal-content">
            <h3>Confirm Export</h3>
            <p>Are you sure you want to start the export with the following settings?</p>
            <ul>
              <li>College: {college}</li>
              <li>Year: {year}</li>
              <li>Departments: {departments || 'All'}</li>
            </ul>
            <div className="admin-modal-actions">
              <button onClick={handleConfirm} className="admin-confirm-btn">Yes, Start Export</button>
              <button onClick={() => setShowConfirmation(false)} className="admin-cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
